import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import KineticHeading from "@/components/KineticHeading";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { LAB } from "@/data/lab";

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <SEO
        title="Contact Lifeline Diagnostics — Address, Phone & Google Maps | Jaysingpur"
        description="Visit Lifeline Diagnostics at Shop No. 2, Rising Sun Apartment, 8th Lane, Near Vairan Adda, Jaysingpur. Call +91 7744999622 or +91 7219789622. Open Mon–Sat 8:00 AM – 8:00 PM."
        keywords="Lifeline Diagnostics contact, diagnostic lab address Jaysingpur, pathology lab phone Jaysingpur, Lifeline Diagnostics Google Maps, Rising Sun Apartment Jaysingpur"
        path="/contact"
      />
      <section className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8 text-navy"
          >
            <span className="h-px w-10 bg-navy" />
            <span className="font-mono-tag">— Visit / Reach out</span>
          </motion.div>

          <KineticHeading
            lines={["Find us,", <em key="e" className="italic font-medium text-cyan-brand">or call us.</em>]}
            className="font-display text-[14vw] md:text-[9vw] leading-[0.88] tracking-[-0.03em] text-navy"
          />

          <div className="mt-14 grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 md:col-span-6">
              <p className="text-slate-600 leading-relaxed max-w-lg">
                No appointments needed. Walk in during working hours or
                ring the lab and Dr. Attar's team will guide you through
                sample collection, timelines and reporting.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="col-span-12 md:col-span-6 md:pl-8">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="border-t hair pt-4">
                  <div className="font-mono-tag text-slate-500">Working Hours</div>
                  <div className="mt-2 font-display text-2xl text-navy">
                    Mon – Sat
                  </div>
                  <div className="text-slate-600 mt-1">8:00 AM – 8:00 PM</div>
                </div>
                <div className="border-t hair pt-4">
                  <div className="font-mono-tag text-slate-500">Sunday</div>
                  <div className="mt-2 font-display text-2xl text-navy">
                    By call
                  </div>
                  <div className="text-slate-600 mt-1">Emergencies only</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {[
              {
                icon: Phone,
                label: "Call",
                value: `${LAB.phone}  /  ${LAB.phone2}`,
                href: LAB.phoneHref,
                testid: "contact-phone",
                dark: true,
              },
              {
                icon: Mail,
                label: "Email",
                value: LAB.email,
                href: LAB.emailHref,
                testid: "contact-email",
              },
              {
                icon: MapPin,
                label: "Address",
                value: LAB.address,
                href: LAB.mapsUrl,
                testid: "contact-address",
                external: true,
              },
              {
                icon: Clock,
                label: "Established",
                value: `Since ${LAB.established}`,
                href: null,
                testid: "contact-established",
              },
            ].map((card, i) => {
              const Wrapper = card.href ? "a" : "div";
              const wrapperProps = card.href
                ? {
                    href: card.href,
                    target: card.external ? "_blank" : undefined,
                    rel: card.external ? "noreferrer" : undefined,
                  }
                : {};
              return (
                <Reveal
                  key={card.label}
                  delay={i * 0.06}
                  className={`col-span-12 md:col-span-6 ${
                    card.dark
                      ? "bg-navy text-white grain"
                      : "bg-paper text-navy border hair"
                  } group`}
                >
                  <Wrapper
                    {...wrapperProps}
                    data-testid={card.testid}
                    className="block p-8 md:p-10 h-full"
                  >
                    <div className="flex items-start justify-between">
                      <card.icon
                        className={`h-8 w-8 ${
                          card.dark ? "text-cyan-brand" : "text-cyan-brand"
                        }`}
                        strokeWidth={1.5}
                      />
                      {card.href && (
                        <ArrowUpRight
                          className={`h-5 w-5 ${
                            card.dark ? "text-white/70" : "text-navy/60"
                          } group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500`}
                        />
                      )}
                    </div>
                    <div className="mt-14">
                      <div
                        className={`font-mono-tag ${
                          card.dark ? "text-cyan-brand" : "text-slate-500"
                        }`}
                      >
                        {card.label}
                      </div>
                      <div
                        className={`mt-3 font-display text-2xl md:text-3xl leading-tight ${
                          card.dark ? "text-white" : "text-navy"
                        }`}
                      >
                        {card.value}
                      </div>
                    </div>
                  </Wrapper>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MAP moved to Home page */}
    </div>
  );
}
